import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import {
  ShimmerBadge,
  ShimmerText,
  ShimmerThumbnail,
  ShimmerTitle,
} from 'react-shimmer-effects'
import { Col, Row } from 'react-bootstrap'
import { PageHeader } from '../common'
import API from '../../utils/API'
import { useErrorService, useUserService } from '@abroad/components'
import QuestsFaculty from './QuestsFaculty'
import EnrollmentProcess from './EnrollmentProcess'
import QuestDetail from './QuestDetail'
import QuestImportantItems from './QuestImportantItems'
import QuestComplimentarySupport from './QuestComplimentarySupport'
import QuestLearningResourcesDashboard from './QuestLearningResourcesDashboard'
import { isQuestPaymentSatisfied } from './questPaymentUtils'
import { useLocation, useHistory } from 'react-router-dom'
import { gtmOpenYourExperiencePageAction } from './gtmfunctions'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const QuestYourHome = () => {
  const [questsHome, setQuestsHome] = useState([])
  const { user } = useUserService()
  const Error = useErrorService()
  const [isLoading, setIsLoading] = useState(true)
  const [allQuests, setAllQuests] = useState([])
  const [selectedQuest, setSelectedQuest] = useState(null)
  const [hasMadePayment, setHasMadePayment] = useState(false)
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useState(false)
  const [isPaymentStatusLoaded, setIsPaymentStatusLoaded] = useState(false)
  const [questSteps, setQuestSteps] = useState(null)
  const [isQuestStepsLoading, setIsQuestStepsLoading] = useState(true)
  const query = useQuery()
  const history = useHistory()
  const questIdFromQuery = query.get('questId')

  useEffect(() => {
    if (selectedQuest) {
      gtmOpenYourExperiencePageAction(
        selectedQuest._id,
        selectedQuest.title,
        user?._id,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuest])

  useEffect(() => {
    const getQuestsHome = async () => {
      setIsLoading(true)
      try {
        const { data: quest } = await API.quests.questsHomeGet()
        if (Object.keys(quest).length === 0) {
          const { data: questAll } = await API.quests.allQuest()
          setQuestsHome(questAll[0])
        } else {
          setQuestsHome(quest)
        }
      } catch (e) {
        Error.showError(e)
      } finally {
        setIsLoading(false)
      }
    }

    getQuestsHome()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchAllQuests = async () => {
      try {
        const { data: questAll } = await API.quests.allQuest()
        setAllQuests(questAll || [])
        if (questIdFromQuery) {
          const quest = (questAll || []).find((q) => q._id === questIdFromQuery)
          setSelectedQuest(quest || questAll?.[0] || null)
        } else {
          setSelectedQuest(questAll?.[0] || null)
        }
      } catch (e) {
        Error.showError(e)
      }
    }
    fetchAllQuests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questIdFromQuery])

  useEffect(() => {
    if (questIdFromQuery && allQuests.length > 0) {
      const quest = allQuests.find((q) => q._id === questIdFromQuery)
      if (quest && quest._id !== selectedQuest?._id) {
        setSelectedQuest(quest)
      }
    }
  }, [questIdFromQuery, allQuests, selectedQuest?._id])

  useEffect(() => {
    const questId = selectedQuest?._id

    setHasMadePayment(false)
    setIsOnboardingCompleted(false)
    setIsPaymentStatusLoaded(false)
    setQuestSteps(null)

    if (!questId) return

    let isCancelled = false
    setIsQuestStepsLoading(true)

    const fetchQuestSteps = async () => {
      try {
        const { data } = await API.quests.getQuestsSteps(questId)
        if (isCancelled) return

        const onboardingDone = data?.onboardingCompleted || false
        const paymentMade = data?.questPayments
          ? isQuestPaymentSatisfied(data.questPayments)
          : false

        setQuestSteps(data)
        setIsOnboardingCompleted(onboardingDone)
        setHasMadePayment(paymentMade)

        // Keep shimmer if parent will swap to Important Items UI
        if (!(onboardingDone && paymentMade)) {
          setIsQuestStepsLoading(false)
        }
      } catch (e) {
        if (isCancelled) return
        Error.showError(e)
        setHasMadePayment(false)
        setIsOnboardingCompleted(false)
        setIsQuestStepsLoading(false)
      } finally {
        if (isCancelled) return
        setIsPaymentStatusLoaded(true)
      }
    }

    fetchQuestSteps()
    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuest?._id])

  const showImportantItems =
    isPaymentStatusLoaded && isOnboardingCompleted && hasMadePayment

  const renderEnrollmentStatusShimmer = () => (
    <div className='mt-64px'>
      <ShimmerThumbnail
        className='border-radius-10 mb-0'
        height={218}
        rounded
      />
      <div className='mt-64px'>
        <Row>
          {Array.from({ length: 2 }).map((_, index) => (
            <Col sm={12} key={index}>
              <ShimmerThumbnail height={150} rounded />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  )

  return (
    <>
      <Helmet>
        <title>
          {isLoading || !questsHome
            ? 'Your Experiences'
            : `${questsHome?.title} | Abroad`}
        </title>
        <meta
          name='title'
          content={
            isLoading || !questsHome
              ? 'Your Experiences'
              : `${questsHome?.title} | Abroad`
          }></meta>
        <meta
          property='og:title'
          content={
            isLoading || !questsHome
              ? 'Your Experiences'
              : `${questsHome?.title} | Abroad`
          }></meta>
        <meta
          property='twitter:title'
          content={
            isLoading || !questsHome
              ? 'Your Experiences'
              : `${questsHome?.title} | Abroad`
          }></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/quests`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
      </Helmet>

      <PageHeader title='Your Experiences' />
      {isLoading || !selectedQuest ? (
        <>
          <div>
            <ShimmerThumbnail
              className='shimmer-thumbnail-mediaplayer border-radius-10 mt-32px'
              rounded
              width={'100%'}
              height={400}
            />
          </div>
          <div className='mt-3'>
            <ShimmerBadge width={120} />
          </div>
          <div>
            <ShimmerTitle line={1} />
          </div>
          <div>
            <ShimmerText line={5} gap={15} />
          </div>
          <div className='mt-5'>
            <Row>
              {Array.from({ length: 3 }).map((_, index) => (
                <Col sm={12} md={12} lg={12} key={index}>
                  <ShimmerThumbnail height={150} rounded />
                </Col>
              ))}
            </Row>
          </div>
        </>
      ) : !questsHome ? (
        <div className='s5 p-0 mt-3 text-center'>
          No experience available at the moment
        </div>
      ) : (
        <>
          {allQuests.length > 1 && (
            <div className='d-flex justify-content-end mt-3 quest-select-row'>
              <div className='position-relative custom-select-wrapper'>
                <select
                  className='form-select custom-select s7 border-radius-5 py-2'
                  value={selectedQuest?._id || ''}
                  onChange={(e) => {
                    const quest = allQuests.find(
                      (q) => q._id === e.target.value,
                    )
                    setSelectedQuest(quest)
                    if (quest) {
                      history.replace(`/your-experiences?questId=${quest._id}`)
                    }
                  }}>
                  {allQuests.map((quest) => (
                    <option key={quest._id} value={quest._id}>
                      {quest.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <QuestDetail
            questHomeId={selectedQuest?._id}
            questHome={selectedQuest}
          />

          {!isPaymentStatusLoaded ? (
            renderEnrollmentStatusShimmer()
          ) : showImportantItems ? (
            <>
              <div className='mt-64px'>
                <QuestImportantItems
                  questId={selectedQuest?._id}
                  questTitle={selectedQuest?.title}
                  orientationCall={selectedQuest?.orientationCall}
                  questSteps={questSteps}
                />
              </div>
              <QuestComplimentarySupport
                questId={selectedQuest?._id}
                questTitle={selectedQuest?.title}
                questSteps={questSteps}
                hasComplimentarySupport={selectedQuest?.hasComplimentarySupport}
              />
              <div className='mt-64px'>
                <QuestsFaculty
                  questId={selectedQuest?._id}
                  faculties={selectedQuest?.faculties || []}
                />
              </div>
              <QuestLearningResourcesDashboard questId={selectedQuest?._id} />
            </>
          ) : (
            <>
              <div className='mt-64px'>
                <EnrollmentProcess
                  questId={selectedQuest?._id}
                  questSteps={questSteps}
                  isLoading={isQuestStepsLoading}
                />
              </div>
              <div className='mt-64px'>
                <QuestsFaculty
                  questId={selectedQuest?._id}
                  faculties={selectedQuest?.faculties || []}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
export default QuestYourHome
